import { ruleLanguageSpec } from "../spec/languageSpec.js";

// eslint-disable-next-line valid-jsdoc
/**
 * registerCompletions(langId, { variables, varDocs, varTypes })
 *  - variables: string[]
 *  - varDocs: Map(name -> markdown string)  // optional
 *  - varTypes: Map(name -> 'd'|'s'|'b')     // optional
 */
export function registerCompletions(monaco, langId, opts = {}) {
    const { variables = [], varDocs = new Map(), varTypes = new Map() } = opts;

    function rangeAt(model, position) {
        // For punctuation, getWordUntilPosition is not helpful —
        // provide an empty insertion range at the caret.
        return new monaco.Range(position.lineNumber, position.column, position.lineNumber, position.column);
    }
    function prevChars(model, position, n) {
        const start = Math.max(1, position.column - n);
        return model.getValueInRange(new monaco.Range(position.lineNumber, start, position.lineNumber, position.column));
    }
    function needsTicks(name) {
        return /[^A-Za-z0-9_]/.test(name);
    }
    function baseVar(name) {
        return needsTicks(name) ? "`" + name.replace(/`/g, "\\`") + "`" : name;
    }

    return monaco.languages.registerCompletionItemProvider(langId, {
        triggerCharacters: ["$", "%", "<", "!", "&", "|", "`", "{", ",", "[", "("],
        provideCompletionItems: (model, position) => {
            const range = rangeAt(model, position);
            const prev1 = prevChars(model, position, 1);
            const prev2 = prevChars(model, position, 2);
            const prev3 = prevChars(model, position, 3);

            const suggestions = [];

            // ---------- Snippets ----------
            suggestions.push(
                {
                    label: "if (...) { ... } else { ... }",
                    kind: monaco.languages.CompletionItemKind.Snippet,
                    insertText: "if ($x%d > 5%d) { ${1:true} } else { ${2:false} }",
                    insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                    range,
                },
                {
                    label: "if (...) { ... }",
                    kind: monaco.languages.CompletionItemKind.Snippet,
                    insertText: "if (${1:($x%d > 5%d)}) { ${2:result} }",
                    insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                    range,
                }
            );


            // ---------- Types right after '%' ----------
            // If the user already typed '%', suggest "d|s|b" WITHOUT the '%' to avoid duplication.
            if (prev1 === "%") {
                for (const t of ruleLanguageSpec.primitives) {
                    suggestions.push({
                        label: `%${t}`,
                        kind: monaco.languages.CompletionItemKind.TypeParameter,
                        insertText: t,
                        documentation: `Type \`%${t}\``,
                        range,
                    });
                }
                return { suggestions };
            }

            // ---------- '[' right after '%s' ----------
            if (prev2 === "%s") {
                suggestions.push({
                    label: "%s[",
                    kind: monaco.languages.CompletionItemKind.TypeParameter,
                    insertText: "[",
                    documentation: "String list marker: `%s[`",
                    range,
                });
            }

            // ---------- Operators when the left side looks like a closed operand ----------
            // Use the last 1–3 characters to detect a closed operand (not word-based).
            const left3 = prev3;
            const looksClosedOperand =
                /%[dsb](\[$)?$/.test(left3) // ...%d | %s | %b | %s[
                || /\)$/.test(left3) // ... )
                || /`[^`]*`%[dsb](\[$)?$/.test(left3); // `...`%s[...]

            if (looksClosedOperand) {
                for (const op of ruleLanguageSpec.operators) {
                    suggestions.push({
                        label: op.lexeme,
                        kind: monaco.languages.CompletionItemKind.Operator,
                        insertText: op.lexeme,
                        documentation: `Operator: ${op.kind}`,
                        range,
                    });
                }
            }

            // ---------- Variables ----------
            for (const name of variables) {
                const naked = baseVar(name); // name or `name`
                const typed = varTypes.get(name) || "s";
                const doc = varDocs.get(name) || `Variable \`${name}\``;

                // If the char to the left is '$', insert only the BODY (no '$') to avoid duplication.
                if (prev1 === "$") {
                    suggestions.push(
                        {
                            label: `$${name}`,
                            kind: monaco.languages.CompletionItemKind.Variable,
                            insertText: naked,
                            documentation: { value: doc },
                            range,
                        },
                        {
                            label: `$${name}%${typed}`,
                            kind: monaco.languages.CompletionItemKind.Variable,
                            insertText: `${naked}%${typed}`,
                            documentation: { value: doc + `\n\nInsert with type: \`%${typed}\`` },
                            range,
                        }
                    );
                    if (typed === "s" && ruleLanguageSpec.listCapableTypes.includes("s")) {
                        suggestions.push({
                            label: `$${name}%s[`,
                            kind: monaco.languages.CompletionItemKind.Variable,
                            insertText: `${naked}%s[`,
                            documentation: { value: doc + "\n\nString list: `%s[`" },
                            range,
                        });
                    }
                } else {
                    suggestions.push(
                        {
                            label: `$${name}`,
                            kind: monaco.languages.CompletionItemKind.Variable,
                            insertText: `$${naked}`,
                            documentation: { value: doc },
                            range,
                        },
                        {
                            label: `$${name}%${typed}`,
                            kind: monaco.languages.CompletionItemKind.Variable,
                            insertText: `$${naked}%${typed}`,
                            documentation: { value: doc + `\n\nInsert with type: \`%${typed}\`` },
                            range,
                        }
                    );
                    if (typed === "s" && ruleLanguageSpec.listCapableTypes.includes("s")) {
                        suggestions.push({
                            label: `$${name}%s[`,
                            kind: monaco.languages.CompletionItemKind.Variable,
                            insertText: `$${naked}%s[`,
                            documentation: { value: doc + "\n\nString list: `%s[`" },
                            range,
                        });
                    }
                }
            }

            // ---------- Explicit types (always available) ----------
            // Suggest "%d" as helpers:
            // insert FULL "%d" only if the char on the left is NOT '%'.
            for (const t of ruleLanguageSpec.primitives) {
                suggestions.push({
                    label: `%${t}`,
                    kind: monaco.languages.CompletionItemKind.TypeParameter,
                    insertText: prev1 === "%" ? t : `%${t}`,
                    range,
                });
                if (ruleLanguageSpec.listCapableTypes.includes(t)) {
                    suggestions.push({
                        label: `%${t}[`,
                        kind: monaco.languages.CompletionItemKind.TypeParameter,
                        insertText: prev2 === `%${t}` ? "[" : `%${t}[`,
                        range,
                    });
                }
            }

            return { suggestions };
        },
    });
}
